import { logEvent } from 'firebase/analytics';
import { analyticsInstance } from './firebase';
import { hasAnalyticsConsent } from './consent';
import { getAttributionParams } from './attribution';

/**
 * Track an event to GA4 + Firebase Analytics.
 * Gated by user consent (Consent Mode v2 + helper). All B2C funnel events go through here.
 * First-touch UTMs are merged into every event so conversions remain attributable
 * even when the user accepts consent after entering with a UTM.
 */
export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (!hasAnalyticsConsent()) return;

  const merged = { ...getAttributionParams(), ...params };

  if (analyticsInstance) {
    logEvent(analyticsInstance, name, merged);
  }

  if (window.gtag) {
    window.gtag('event', name, merged);
  }
}

// ── Funnel Events ───────────────────────────────────────────────────────

export const funnel = {
  landingView: (category: string, utmSource?: string) =>
    trackEvent('vida_landing_view', { category, utm_source: utmSource }),

  registerStart: () =>
    trackEvent('vida_register_start'),

  registerComplete: (method: 'google' | 'email') =>
    trackEvent('vida_register_complete', { method }),

  onboardingStep: (step: number, total: number) =>
    trackEvent('vida_onboarding_step', { step, total }),

  onboardingComplete: (category: string, objective: string) =>
    trackEvent('vida_onboarding_complete', { category, objective }),

  planGenerated: (planType: 'free' | 'premium', days: number) =>
    trackEvent('vida_plan_generated', { plan_type: planType, days }),

  planViewed: (dayNumber: number) =>
    trackEvent('vida_plan_viewed', { day_number: dayNumber }),

  recipeViewed: (recipeId: string) =>
    trackEvent('vida_recipe_viewed', { recipe_id: recipeId }),

  scanUsed: (remaining: number) =>
    trackEvent('vida_scan_used', { remaining }),

  shoppingViewed: () =>
    trackEvent('vida_shopping_viewed'),

  productViewed: (productId: string, score: number) =>
    trackEvent('vida_product_viewed', { product_id: productId, score }),

  productsListViewed: (count: number) =>
    trackEvent('vida_products_list_viewed', { product_count: count }),

  dashboardViewed: () =>
    trackEvent('vida_dashboard_viewed'),

  upgradeShown: (trigger: string) =>
    trackEvent('vida_upgrade_shown', { trigger }),

  subscriptionStart: () =>
    trackEvent('vida_subscription_start'),

  subscriptionComplete: (plan: string) =>
    trackEvent('vida_subscription_complete', { plan }),

  healthTestUploaded: (type: 'blood' | 'urine' | 'microbiota' | 'nutrigenetics') =>
    trackEvent('vida_health_test_uploaded', { test_type: type }),

  chatOpened: () =>
    trackEvent('vida_chat_opened'),

  chatMessageSent: (hasMedia: boolean, mediaType?: string) =>
    trackEvent('vida_chat_message_sent', { has_media: hasMedia, media_type: mediaType }),

  chatProductRecommended: (count: number) =>
    trackEvent('vida_chat_product_recommended', { product_count: count }),

  chatFoodAnalyzed: (score: number) =>
    trackEvent('vida_chat_food_analyzed', { compatibility_score: score }),

  chatMediaUploaded: (type: 'image' | 'document' | 'audio') =>
    trackEvent('vida_chat_media_uploaded', { media_type: type }),

  appointmentBooked: () =>
    trackEvent('vida_appointment_booked'),

  // Conversion tracking
  featureGated: (feature: string, tier: string) =>
    trackEvent('vida_feature_gated', { feature, user_tier: tier }),

  upgradeFunnelStage: (stage: 'shown' | 'clicked' | 'started' | 'completed', trigger: string, tier: string) =>
    trackEvent('vida_upgrade_funnel', { stage, trigger, tier }),
};

// gtag type declaration
declare global {
  interface Window {
    gtag?: (command: string, target: string, config?: Record<string, unknown>) => void;
  }
}
